/*
 * TEFL Course Specialism Picker
 *
 * - Limits selections to a configurable max (matches required_count).
 * - Live counter UI + state="complete" data attribute when requirement met.
 * - Disables buy buttons in the closest form until requirement met.
 * - Writes selected titles into hidden inputs as cart line properties.
 * - Validates on form submit; blocks if requirement not met.
 *
 * Initialised once per picker via the [data-specialism-picker-init] guard.
 */
(function () {
  'use strict';

  function findProductForm(picker) {
    // Try the standard nest first
    var f = picker.closest('form');
    if (f) return f;
    // Horizon: form is in a sibling group-block-content, not an ancestor.
    // Pick the cart-add form on the page.
    return document.querySelector(
      'form.shopify-product-form, form[action$="/cart/add"], form[action*="/cart/add"]'
    );
  }

  function init(picker) {
    if (!picker || picker.dataset.specialismPickerInit === 'true') return;
    picker.dataset.specialismPickerInit = 'true';

    var required = parseInt(picker.dataset.requiredCount, 10) || 2;
    var checkboxes = Array.from(picker.querySelectorAll('[data-specialism-checkbox]'));
    var slots = Array.from(picker.querySelectorAll('[data-specialism-slot]'));
    var counterEl = picker.querySelector('[data-counter]');
    var errorEl = picker.querySelector('[data-error]');
    var badgeEl = picker.querySelector('[data-badge]');
    var form = findProductForm(picker);

    // Move hidden slot inputs into the product form so they get submitted.
    // We leave the visible markup in place but relocate the <input type="hidden">.
    if (form) {
      slots.forEach(function (slot) { form.appendChild(slot); });
    }

    // Apply tooltip text + an aria-describedby-friendly title to every buy
    // button in the form. We pull the error message from the picker's error
    // node so it stays in sync with the block setting.
    if (form) {
      var tooltipText = (errorEl && errorEl.textContent.trim()) || 'Please complete your selection.';
      var buyButtons = form.querySelectorAll('button[name="add"], .product-form__submit');
      buyButtons.forEach(function (btn) {
        btn.setAttribute('data-specialism-tooltip', tooltipText);
        btn.setAttribute('title', tooltipText);
        btn.setAttribute('aria-describedby', 'specialism-picker-help');
      });
      if (errorEl) errorEl.id = errorEl.id || 'specialism-picker-help';
    }

    if (!checkboxes.length) return;

    function getChecked() {
      return checkboxes.filter(function (c) { return c.checked; });
    }

    function syncSlots(checked) {
      slots.forEach(function (slot, i) {
        slot.value = checked[i] ? (checked[i].dataset.title || '') : '';
      });
    }

    function syncState() {
      var checked = getChecked();
      var count = checked.length;
      var complete = count === required;

      if (counterEl) counterEl.textContent = String(count);
      picker.dataset.currentCount = String(count);
      picker.dataset.state = complete ? 'complete' : 'incomplete';

      // Disable unchecked boxes once max reached
      checkboxes.forEach(function (c) {
        c.disabled = (count >= required) && !c.checked;
      });

      syncSlots(checked);

      // Toggle the closest form's incomplete flag (for in-form selectors)
      if (form) {
        form.dataset.specialismIncomplete = complete ? 'false' : 'true';
      }

      // Also flag the body so CSS / JS can gate buttons that live OUTSIDE
      // the form — e.g. Horizon's <sticky-add-to-cart> bar.
      document.body.dataset.specialismIncomplete = complete ? 'false' : 'true';

      // Toggle tooltip + aria-disabled on EVERY add-to-cart button on the page
      // (main buy button, sticky bar, anything else that gets added).
      var tooltipText = (errorEl && errorEl.textContent.trim()) || 'Please complete your selection.';
      var allBuyBtns = document.querySelectorAll(
        'button[name="add"], .add-to-cart-button, .sticky-add-to-cart__button, .product-form__submit'
      );
      allBuyBtns.forEach(function (btn) {
        if (complete) {
          btn.removeAttribute('title');
          btn.removeAttribute('aria-disabled');
          btn.removeAttribute('data-specialism-tooltip');
        } else {
          btn.setAttribute('title', tooltipText);
          btn.setAttribute('aria-disabled', 'true');
          btn.setAttribute('data-specialism-tooltip', tooltipText);
        }
      });

      // Hide error once requirement met
      if (errorEl && complete) errorEl.hidden = true;

      // Badge label flip (success ✓ when complete).
      // Stash the original label on first encounter, then always set the
      // visible text from the current `complete` state — so the flip works
      // every time the count transitions, not just on the first completion.
      if (badgeEl) {
        if (!badgeEl.dataset.originalLabel) {
          badgeEl.dataset.originalLabel = badgeEl.textContent.trim();
        }
        var orig = badgeEl.dataset.originalLabel;
        badgeEl.textContent = complete
          ? orig.replace(/select/i, 'Selected') + ' ✓'
          : orig;
      }
    }

    checkboxes.forEach(function (cb) {
      cb.addEventListener('change', syncState);
    });

    // Set initial state
    syncState();

    // Form submission guard — block if requirement not met
    if (form) {
      form.addEventListener('submit', function (e) {
        var count = getChecked().length;
        if (count !== required) {
          e.preventDefault();
          e.stopImmediatePropagation();
          if (errorEl) errorEl.hidden = false;
          picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Brief shake
          picker.animate(
            [
              { transform: 'translateX(0)' },
              { transform: 'translateX(-6px)' },
              { transform: 'translateX(6px)' },
              { transform: 'translateX(-4px)' },
              { transform: 'translateX(4px)' },
              { transform: 'translateX(0)' }
            ],
            { duration: 380, easing: 'ease-out' }
          );
          return false;
        }
      }, true);
    }

    // After the main course is added to the cart, add the chosen specialisms as
    // SEPARATE line items (same mechanism as the Teaching Resources block) so each
    // maps 1:1 to its Moodle course. They are zeroed at checkout by the automatic
    // "bundle → 2 specialisms free" discount.
    var parentProductId = picker.dataset.parentProductId;
    if (parentProductId) {
      var productForm = document.querySelector(
        'product-form-component[data-product-id="' + parentProductId + '"]'
      );
      if (productForm) {
        productForm.addEventListener('cart:update', function (event) {
          var detail = (event && event.detail) || {};
          if (detail.data && detail.data.didError) return;
          if (detail.data && detail.data.source !== 'product-form-component') return;
          if (String(detail.data && detail.data.productId) !== String(parentProductId)) return;

          var items = getChecked()
            .map(function (c) { return { id: Number(c.dataset.variantId), quantity: 1 }; })
            .filter(function (i) { return i.id; });
          if (!items.length) return;

          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: items })
          })
            .then(function (res) {
              if (!res.ok) {
                return res.text().then(function (t) {
                  console.warn('[specialism-picker] cart/add failed:', t);
                });
              }
              return fetch('/cart.js')
                .then(function (r) { return r.ok ? r.json() : null; })
                .then(function (cart) {
                  if (!cart) return;
                  productForm.dispatchEvent(new CustomEvent('cart:update', {
                    bubbles: true,
                    detail: { resource: cart, data: { source: 'specialism-picker', didError: false } }
                  }));
                });
            })
            .catch(function (err) { console.warn('[specialism-picker] error:', err); });
        });
      }
    }
  }

  function initAll() {
    document.querySelectorAll('.course-specialism-picker').forEach(init);
  }

  // Global capture-phase guard for the sticky bar (and any other ATC buttons
  // outside the product form). If the picker is incomplete when a buy button
  // is clicked, block the click AT capture so the sticky bar's own handler
  // never fires.
  document.addEventListener('click', function (e) {
    if (document.body.dataset.specialismIncomplete !== 'true') return;
    var t = e.target;
    if (!(t instanceof Element)) return;
    var btn = t.closest(
      'button[name="add"], .add-to-cart-button, .sticky-add-to-cart__button, .product-form__submit'
    );
    if (!btn) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    // Scroll first picker into view + shake
    var picker = document.querySelector('.course-specialism-picker');
    if (picker) {
      var errorEl = picker.querySelector('[data-error]');
      if (errorEl) errorEl.hidden = false;
      picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
      picker.animate(
        [
          { transform: 'translateX(0)' },
          { transform: 'translateX(-6px)' },
          { transform: 'translateX(6px)' },
          { transform: 'translateX(-4px)' },
          { transform: 'translateX(4px)' },
          { transform: 'translateX(0)' }
        ],
        { duration: 380, easing: 'ease-out' }
      );
    }
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init on section re-render (Horizon's section rendering API)
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:block:select', initAll);
})();
